"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pagestateFingerprint = exports.renderToken = void 0;
const normalizer_1 = require("./normalizer");
const tokenEscape_1 = require("./tokenEscape");
// Treated as "no role": never emitted as `[role=...]`, even when the token
// requests the role field.
const GENERIC_ROLES = new Set([
    '',
    'other',
    'none',
    'unknown',
    'genericelement'
]);
const asRecord = (value) => value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
const simpleClass = (className) => className ? className.split('.').pop() : '';
/**
 * This priority order is DELIBERATELY different from `textNorm.nodeText`, which
 * serves the offspring/enrichment descriptors rather than this fingerprint.
 * An empty string must fall through to the next source, hence `||` (not `??`).
 */
const pagestateNodeText = (node) => {
    const t = node.label || node.text || node.contentDescription || '';
    return String(t).trim();
};
/**
 * Deliberately NOT routed through the normalizer, unlike
 * `generateViewSelector`'s role field: this fingerprint's role token is meant
 * to be coarser than the element selector's.
 */
const roleOf = (node) => {
    const props = asRecord(node.calculatedProps);
    const role = (typeof props.role === 'string' ? props.role : '') ||
        node.elementType ||
        node.calculatedRole ||
        '';
    return String(role).toLowerCase();
};
/**
 * Text for the `[txt=...]` token. Truncation counts code points (`Array.from`,
 * not `.slice`) so astral-plane characters are not split mid-character. The
 * newline and `[`/`]`/`\` neutralization is what stops node text forging an
 * extra `Substructure:` line or an extra token boundary.
 */
const renderedText = (node, normalizer) => {
    let text = pagestateNodeText(node);
    if (!text) {
        return '';
    }
    const codePoints = Array.from(text);
    if (codePoints.length > 30) {
        text = `${codePoints.slice(0, 27).join('')}...`;
    }
    if (normalizer !== null) {
        text = normalizer.normalize(text, 'label');
    }
    return (0, tokenEscape_1.escapeTokenValue)(text);
};
/**
 * Render one node's token: `<Class>[role=..][txt=..]`. Bracket order is fixed
 * here — `tokenFields` is an unordered set of requested fields, not a
 * sequence.
 */
const renderToken = (node, cfg, normalizer) => {
    const tokenFields = new Set(cfg.tokenFields);
    const cls = simpleClass(String(node.className || '')) || 'unknown';
    const parts = [(0, tokenEscape_1.escapeTokenValue)(cls)];
    if (tokenFields.has('role')) {
        const role = roleOf(node);
        if (role && !GENERIC_ROLES.has(role)) {
            parts.push(`[role=${(0, tokenEscape_1.escapeTokenValue)(role)}]`);
        }
    }
    if (tokenFields.has('text')) {
        const text = renderedText(node, normalizer);
        if (text) {
            parts.push(`[txt=${text}]`);
        }
    }
    return parts.join('');
};
exports.renderToken = renderToken;
/**
 * True when `node` is descended through without emitting a token of its own,
 * its children attaching to its parent's block instead. Text wins over the
 * denylist: a node carrying real text is salient whatever its class.
 */
const isTransparent = (node, wrapperDenylist) => {
    if (pagestateNodeText(node)) {
        return false;
    }
    const cn = simpleClass(String(node.className || ''));
    if (wrapperDenylist.has(cn)) {
        return true;
    }
    const props = asRecord(node.calculatedProps);
    const role = String((typeof props.role === 'string' ? props.role : '') ||
        node.elementType ||
        node.calculatedRole ||
        '').toLowerCase();
    return GENERIC_ROLES.has(role);
};
/**
 * `depth` is absolute: descending through a transparent node does not
 * increment it.
 */
const buildBlocks = (node, depth, cfg, wrapperDenylist, normalizer) => {
    if (depth > cfg.salientDepth) {
        return [];
    }
    const blocks = [];
    for (const child of node.children ?? []) {
        if (isTransparent(child, wrapperDenylist)) {
            blocks.push(...buildBlocks(child, depth, cfg, wrapperDenylist, normalizer));
        }
        else {
            const token = (0, exports.renderToken)(child, cfg, normalizer);
            const childBlocks = buildBlocks(child, depth + 1, cfg, wrapperDenylist, normalizer);
            const sig = token + childBlocks.map(cb => `|${cb.sig}`).join('');
            blocks.push({ depth, node: child, token, children: childBlocks, sig });
        }
    }
    const collapsed = [];
    for (const b of blocks) {
        if (collapsed.length > 0 && collapsed[collapsed.length - 1].sig === b.sig) {
            continue;
        }
        collapsed.push(b);
    }
    return collapsed;
};
const flattenBlocks = (blocks) => {
    const flat = [];
    for (const b of blocks) {
        flat.push(b);
        flat.push(...flattenBlocks(b.children));
    }
    return flat;
};
/**
 * Emit ALL nodes up to and including the Nth labeled level (a salient depth
 * at which >= 1 emitted node has non-empty `pagestateNodeText`); stop after
 * it. Every node on each kept level is emitted (no mid-level cuts).
 */
const bfsLabeledLines = (root, cfg, wrapperDenylist, normalizer) => {
    const flat = flattenBlocks(buildBlocks(root, 1, cfg, wrapperDenylist, normalizer));
    const levels = new Map();
    for (const block of flat) {
        const list = levels.get(block.depth) ?? [];
        list.push(block);
        levels.set(block.depth, list);
    }
    const lines = [];
    let labeledCount = 0;
    for (let depth = 1; depth <= cfg.salientDepth; depth++) {
        const levelNodes = levels.get(depth);
        if (!levelNodes || levelNodes.length === 0) {
            continue;
        }
        for (const block of levelNodes) {
            lines.push([depth, block.token]);
        }
        if (levelNodes.some(b => pagestateNodeText(b.node))) {
            labeledCount += 1;
            if (labeledCount >= cfg.labeledLevels) {
                break;
            }
        }
    }
    return lines;
};
/**
 * Build the screen fingerprint for `tree`'s root: `Element: <root token>` plus
 * a `Substructure:` block of lines, 2-space-indented per depth. When the
 * block would exceed `pagestateCfg.substructureMaxLines`, only the first
 * `substructureMaxLines` lines are kept and a trailing `  …+N` marker
 * records how many were dropped. The marker carries the count, not the dropped
 * content, so two screens sharing both the kept prefix and the overflow count
 * collide. That bound is accepted: an unbounded fingerprint costs more than
 * the collision does. Returns `''` when `tree` has no root.
 *
 * Uses the same `buildFieldNormalizer()` pipeline the element signature does,
 * rather than a second one of its own.
 */
const pagestateFingerprint = (tree, pagestateCfg) => {
    const root = tree.root;
    if (!root) {
        return '';
    }
    const normalizer = (0, normalizer_1.buildFieldNormalizer)();
    const wrapperDenylist = new Set(pagestateCfg.wrapperDenylist);
    const element = (0, exports.renderToken)(root, pagestateCfg, normalizer);
    const rendered = bfsLabeledLines(root, pagestateCfg, wrapperDenylist, normalizer);
    const parts = [`Element: ${element}`];
    const kept = rendered.length > pagestateCfg.substructureMaxLines
        ? rendered.slice(0, pagestateCfg.substructureMaxLines)
        : rendered;
    if (kept.length > 0) {
        parts.push('Substructure:');
        for (const [depth, token] of kept) {
            parts.push('  '.repeat(depth) + token);
        }
        if (rendered.length > kept.length) {
            parts.push(`  …+${rendered.length - kept.length}`);
        }
    }
    return parts.join('\n');
};
exports.pagestateFingerprint = pagestateFingerprint;
//# sourceMappingURL=pagestateFingerprint.js.map